import logoZofia from '@/assets/logo-zofia.png';
import { Instagram, Facebook, Mail, Phone, MessageCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import SmartLink from '@/components/SmartLink';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const links = {
    marketing: [
      { label: 'Marketing Agency', href: '/marketing-agency' },
      { label: 'Servizi B2B', href: '/servizi/b2b' },
      { label: 'Servizi B2C', href: '/servizi/b2c' },
      { label: 'Promozioni', href: '/promozioni' },
    ],
    digital: [
      { label: 'Digital Solutions', href: '/digital-solutions' },
      { label: 'Automazioni AI', href: '/soluzioni/automazioni-ai' },
      { label: 'CRM Personalizzato', href: '/soluzioni/crm' },
      { label: 'Software Gestionali', href: '/soluzioni/gestionali' },
      { label: 'Integrazioni', href: '/soluzioni/integrazioni' },
    ],
    main: [
      { label: 'Servizi', href: '/servizi' },
      { label: 'Marketing Agency', href: '/marketing-agency' },
      { label: 'Digital Solutions', href: '/digital-solutions' },
      { label: 'Promozioni', href: '/promozioni' },
      { label: 'Insights', href: '/insights' },
      { label: 'Contatti', href: '/contatti' },
    ],
    contact: [
      { label: 'info@zofiadigitalagency.com', href: 'mailto:info@zofiadigitalagency.com', icon: Mail },
      { label: '+39 329 400 7889', href: 'tel:+393294007889', icon: Phone },
      { label: 'WhatsApp', href: 'https://api.whatsapp.com/send?phone=393294007889', icon: MessageCircle },
    ],
    legal: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Cookie Policy', href: '/cookie' },
      { label: 'Sitemap', href: '/sitemap.xml', external: true },
    ],
  };

  const socials = [
    { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61582698747996', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/zofia.digitalagency', label: 'Instagram' },
  ];

  return (
    <footer className="bg-navy-deep border-t border-border">
      <div className="container-premium py-10 md:py-12">
        {/* Desktop layout */}
        <div className="hidden md:grid grid-cols-5 gap-8">
          {/* Logo & Socials */}
          <div>
            <img src={logoZofia} alt="Zofia Digital Agency" width={112} height={112} loading="lazy" className="h-20 w-auto mb-5 opacity-80" />
            <div className="flex gap-3">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-9 h-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-gold hover:border-gold transition-colors"
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Marketing Agency */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider uppercase mb-4">Marketing Agency</h4>
            <ul className="space-y-3">
              {links.marketing.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-muted-foreground hover:text-gold link-underline transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Digital Solutions */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider uppercase mb-4">Digital Solutions</h4>
            <ul className="space-y-3">
              {links.digital.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-muted-foreground hover:text-gold link-underline transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contatti */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider uppercase mb-4">Contatti</h4>
            <ul className="space-y-3">
              {links.contact.map((item) => (
                <li key={item.label}>
                  <a 
                    href={item.href} 
                    target={item.href.startsWith('https') ? '_blank' : undefined}
                    rel={item.href.startsWith('https') ? 'noopener noreferrer' : undefined}
                    className="text-muted-foreground hover:text-gold link-underline transition-colors text-sm inline-flex items-center gap-2"
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider uppercase mb-4">Legal</h4>
            <ul className="space-y-3">
              {links.legal.map((link) => (
                <li key={link.label}>
                  {'external' in link && link.external ? (
                    <a href={link.href} className="text-muted-foreground hover:text-gold link-underline transition-colors text-sm">
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.href} className="text-muted-foreground hover:text-gold link-underline transition-colors text-sm">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Mobile layout */}
        <div className="md:hidden">
          <div className="flex items-start justify-between gap-4 mb-6">
            <img src={logoZofia} alt="Zofia Digital Agency" width={112} height={112} loading="lazy" className="h-16 w-auto opacity-80" />
            <div className="flex gap-2">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-gold hover:border-gold transition-colors"
                >
                  <social.icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </div>

          <Accordion type="single" collapsible>
            <AccordionItem value="links" className="border-border">
              <AccordionTrigger className="py-3 text-sm tracking-wider uppercase">Link</AccordionTrigger>
              <AccordionContent className="pb-3">
                <ul className="space-y-3">
                  {links.main.map((link) => (
                    <li key={link.label}>
                      <Link to={link.href} className="text-muted-foreground hover:text-gold text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                  {links.legal.map((link) => (
                    <li key={link.label}>
                      <Link to={link.href} className="text-muted-foreground hover:text-gold text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="contact" className="border-border">
              <AccordionTrigger className="py-3 text-sm tracking-wider uppercase">Contatti</AccordionTrigger>
              <AccordionContent className="pb-3">
                <ul className="space-y-3">
                  {links.contact.map((item) => (
                    <li key={item.label}>
                      <a 
                        href={item.href}
                        target={item.href.startsWith('https') ? '_blank' : undefined}
                        rel={item.href.startsWith('https') ? 'noopener noreferrer' : undefined}
                        className="text-muted-foreground hover:text-gold text-sm inline-flex items-center gap-2"
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-5 border-t border-border">
        <div className="flex flex-col md:flex-row md:justify-between gap-1">
          <p className="text-xs text-muted-foreground text-center md:text-left">
            © {currentYear} Zofia Digital Agency. Tutti i diritti riservati.
          </p>
          <p className="text-xs text-muted-foreground text-center md:text-right">
            P.IVA 01942060706
          </p>
        </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
